import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from 'recharts';
import api from '../services/api';
import JDMAReplyModal from './JDMAReplyModal';
import { PERIOD_PRESETS, computeDateRange } from '../utils/jdmaPeriod';
import { compressFile, compressPdfClientSide } from '../utils/documents';
import { CommentDocThumb } from './shared/DocumentViewer';
import './JDMAVue.css';
import { immatCorrespond } from '../utils/immat';

const CANAL_LABELS = { sms: 'SMS', mail: 'Mail', telephone: 'Téléphone' };

const FILTRES_NOTE = [
  { value: 'all',   label: 'Toutes notes' },
  { value: '5',     label: '⭐ 5 étoiles' },
  { value: '4',     label: '⭐ 4 étoiles' },
  { value: 'alert', label: '⚠️ ≤ 3 étoiles' },
  { value: 'comm',  label: '💬 Avec commentaire' },
];

function Stars({ note }) {
  if (note == null) {
    return (
      <span
        className="jdma-no-note"
        title="L'enquête JDMA source ne contient pas de question de note pour cet avis"
      >
        — Sans note
      </span>
    );
  }
  const full = Math.floor(note);
  return (
    <span className="jdma-stars" title={`${note}/5`}>
      {'★'.repeat(full)}<span className="jdma-stars-empty">{'★'.repeat(5 - full)}</span>
    </span>
  );
}

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return d; }
}

function formatSemaine(s) {
  if (!s) return '';
  const d = new Date(s);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

function JDMAVue({ type, user, concessions = [], periodFilter, setPeriodFilter }) {
  // Fallback local si le composant est monté sans props (rétro-compat / tests).
  const [localPeriod, setLocalPeriod] = useState({ preset: '30d', from: null, to: null });
  const effectivePeriod = periodFilter || localPeriod;
  const updatePeriod = setPeriodFilter || setLocalPeriod;

  const [filtreConc, setFiltreConc] = useState('');
  const [filtreNote, setFiltreNote] = useState('all');
  const [search, setSearch]         = useState('');

  const [stats, setStats]           = useState(null);
  const [evolution, setEvolution]   = useState([]);
  const [collaborateurs, setCollab] = useState([]);
  const [avisAll, setAvisAll]       = useState([]);   // tous les avis sur la période + concession
  const [commentCounts, setCommentCounts] = useState({}); // { avis_id: n }
  const [selectedCollab, setSelectedCollab] = useState(null);
  const [expandedAvis, setExpandedAvis] = useState(null); // id de l'avis dont les commentaires sont dépliés
  const [loading, setLoading]       = useState(true);
  const [toast, setToast]           = useState(null);
  const [replyAvis, setReplyAvis]   = useState(null);

  const isSuperAdmin = user?.role === 'super-admin';

  const showToast = (msg, kind = 'success') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 4000);
  };

  // Compteur de commentaires : setter stable + idempotent.
  // Indispensable pour éviter la boucle de re-render de <JDMAInternalComments>
  // (sa prop onCountChange doit être référentiellement stable).
  const updateCommentCount = useCallback((avisId, n) => {
    setCommentCounts(prev => (prev[avisId] === n ? prev : { ...prev, [avisId]: n }));
  }, []);

  // Calcul des dates from/to selon le filtre période partagé
  const { from, to } = useMemo(() => computeDateRange(effectivePeriod), [effectivePeriod]);

  // Pour le calcul recharts : nombre approximatif de semaines couvertes par la période
  const weeksCount = useMemo(() => {
    if (!from || !to) return 4;
    const ms = new Date(to) - new Date(from);
    return Math.min(Math.max(Math.ceil(ms / (7 * 86400 * 1000)), 1), 26);
  }, [from, to]);

  // Chargement complet : KPIs + évolution + collaborateurs + tous les avis.
  // AbortController : si l'utilisateur change rapidement de période, la réponse
  // de l'ancienne requête est ignorée — évite que la liste affichée corresponde
  // à un fetch obsolète (cause possible du bug 30j/90j incohérent).
  useEffect(() => {
    const ac = new AbortController();
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ from, to });
        if (filtreConc) params.set('concession_id', filtreConc);

        const evoParams = new URLSearchParams({ weeks: String(weeksCount) });
        if (filtreConc) evoParams.set('concession_id', filtreConc);

        const opt = { signal: ac.signal };
        const [statsRes, evoRes, colRes, avisRes] = await Promise.all([
          api.get(`/jdma/${type}/stats?${params}`, opt),
          api.get(`/jdma/${type}/evolution?${evoParams}`, opt),
          api.get(`/jdma/${type}/par-collaborateur?${params}`, opt),
          api.get(`/jdma/${type}?${params}`, opt),
        ]);
        if (cancelled || ac.signal.aborted) return;
        setStats(statsRes.data);
        setEvolution(evoRes.data.series || []);
        setCollab(colRes.data.collaborateurs || []);
        const entries = avisRes.data.entries || [];
        setAvisAll(entries);

        if (entries.length) {
          const ids = entries.map(e => e.id).filter(Boolean).join(',');
          if (ids) {
            api.get(`/jdma/${type}/comment-counts?ids=${ids}`, opt)
              .then(r => {
                if (!cancelled && !ac.signal.aborted) {
                  setCommentCounts(r.data.counts || {});
                }
              })
              .catch(() => {
                if (!cancelled && !ac.signal.aborted) setCommentCounts({});
              });
          } else {
            setCommentCounts({});
          }
        } else {
          setCommentCounts({});
        }
      } catch (err) {
        if (cancelled || ac.signal.aborted) return;
        if (err.name === 'CanceledError' || err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
        console.error('Erreur chargement JDMA:', err);
        showToast('Erreur de chargement', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [type, from, to, filtreConc, weeksCount]);

  // Reset auto du collaborateur sélectionné quand on change de concession,
  // de période ou de type — évite les listes vides "fantômes" si le collab
  // n'existe plus dans le nouveau périmètre.
  useEffect(() => {
    setSelectedCollab(null);
    setExpandedAvis(null);
  }, [filtreConc, effectivePeriod, type]);

  // Filtrage local des avis : par collab sélectionné + filtre note + recherche
  const avisFiltres = useMemo(() => {
    let list = avisAll;
    if (selectedCollab) {
      list = list.filter(a => a.collaborateur === selectedCollab.collaborateur);
    }
    if (filtreNote === '5') list = list.filter(a => a.note === 5);
    else if (filtreNote === '4') list = list.filter(a => a.note === 4);
    else if (filtreNote === 'alert') list = list.filter(a => a.note != null && a.note <= 3);
    else if (filtreNote === 'comm') list = list.filter(a => a.commentaire && a.commentaire.trim());
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(a => {
        const d = a.data || {};
        return (
          (d['Nom Feedbacker'] || '').toLowerCase().includes(q) ||
          (d['Prénom Feedbacker'] || '').toLowerCase().includes(q) ||
          immatCorrespond(d['immatriculation'], q) ||
          (a.commentaire || '').toLowerCase().includes(q)
        );
      });
    }
    return list;
  }, [avisAll, selectedCollab, filtreNote, search]);

  const collabFiltres = useMemo(() => {
    if (!search) return collaborateurs;
    const q = search.toLowerCase();
    return collaborateurs.filter(c => (c.collaborateur || '').toLowerCase().includes(q));
  }, [collaborateurs, search]);

  const handleRecontact = async (a, canal) => {
    try {
      const r = await api.put(`/jdma/${type}/${a.id}/recontact`, { canal });
      setAvisAll(prev => prev.map(av =>
        av.id === a.id
          ? { ...av, recontacte_le: r.data.recontacte_le, canal_recontact: r.data.canal_recontact }
          : av
      ));
      showToast('Recontact enregistré', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Erreur recontact', 'error');
    }
  };

  const handleRecontactReset = async (a) => {
    try {
      await api.delete(`/jdma/${type}/${a.id}/recontact`);
      setAvisAll(prev => prev.map(av =>
        av.id === a.id ? { ...av, recontacte_le: null, canal_recontact: null } : av
      ));
      showToast('Recontact annulé', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Erreur', 'error');
    }
  };

  const concessionsActives = useMemo(() => {
    if (!stats) return concessions;
    return concessions; // on laisse toutes les concessions pour permettre changement de filtre
  }, [concessions, stats]);

  return (
    <div className="jdma-vue">
      {/* Toolbar */}
      <div className="jdma-vue-toolbar">
        <input
          className="page-search"
          placeholder="🔍 Rechercher collaborateur, client, immat..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className={`page-select ${effectivePeriod.preset !== '30d' ? 'active-filter' : ''}`}
          value={effectivePeriod.preset}
          onChange={(e) => updatePeriod({ ...effectivePeriod, preset: e.target.value })}
        >
          {PERIOD_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        {effectivePeriod.preset === 'custom' && (
          <>
            <input
              type="date"
              className="page-select"
              value={effectivePeriod.from || ''}
              onChange={(e) => updatePeriod({ ...effectivePeriod, from: e.target.value })}
              title="Date de début"
            />
            <input
              type="date"
              className="page-select"
              value={effectivePeriod.to || ''}
              onChange={(e) => updatePeriod({ ...effectivePeriod, to: e.target.value })}
              title="Date de fin"
            />
          </>
        )}
        {isSuperAdmin && (
          <select
            className={`page-select ${filtreConc ? 'active-filter' : ''}`}
            value={filtreConc}
            onChange={(e) => setFiltreConc(e.target.value)}
          >
            <option value="">Toutes concessions</option>
            {concessionsActives.map(c => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </select>
        )}
        <select
          className={`page-select ${filtreNote !== 'all' ? 'active-filter' : ''}`}
          value={filtreNote}
          onChange={(e) => setFiltreNote(e.target.value)}
          title="Filtrer les avis affichés"
        >
          {FILTRES_NOTE.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>

      {/* KPIs */}
      <div className="jdma-vue-kpis">
        <KPICard
          label="Note moyenne"
          big={stats?.note_moyenne != null ? stats.note_moyenne.toFixed(2) : '—'}
          subtitle={stats?.note_moyenne != null ? <Stars note={stats.note_moyenne} /> : null}
          color="primary"
        />
        <KPICard
          label="Total avis"
          big={stats?.total ?? '—'}
          subtitle={stats?.total_avec_note != null ? `${stats.total_avec_note} avec note` : null}
        />
        <KPICard
          label="Excellence (5 étoiles)"
          big={stats?.pct_5 != null ? `${stats.pct_5}%` : '—'}
          subtitle={stats?.nb_5 != null ? `${stats.nb_5} avis 5★` : null}
          color="success"
        />
        <KPICard
          label="Alerte (≤ 3 étoiles)"
          big={stats?.pct_alerte != null ? `${stats.pct_alerte}%` : '—'}
          subtitle={stats?.nb_alerte != null ? `${stats.nb_alerte} avis ≤3★` : null}
          color={stats?.pct_alerte > 10 ? 'danger' : 'neutral'}
        />
      </div>

      {/* Graphe évolution */}
      <div className="jdma-vue-card">
        <h3 className="jdma-vue-card-title">Évolution hebdomadaire</h3>
        {evolution.length === 0 ? (
          <div className="jdma-vue-empty">Pas encore de données pour cette période.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={evolution.map(s => ({
              ...s,
              semaine_label: formatSemaine(s.semaine),
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" />
              <XAxis dataKey="semaine_label" stroke="var(--text-secondary)" />
              <YAxis yAxisId="left" domain={[0, 5]} stroke="var(--text-secondary)" label={{ value: 'Note moy.', angle: -90, position: 'insideLeft', fill: 'var(--text-secondary)' }} />
              <YAxis yAxisId="right" orientation="right" stroke="var(--text-secondary)" label={{ value: 'Nb avis', angle: 90, position: 'insideRight', fill: 'var(--text-secondary)' }} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="right" dataKey="nb_avis" fill="var(--color-info)" name="Nb avis" />
              <Line yAxisId="left" type="monotone" dataKey="note_moyenne" stroke="var(--color-accent)" strokeWidth={3} name="Note moyenne" dot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Layout 2 colonnes : collaborateurs (gauche) + avis (droite) */}
      <div className="jdma-vue-grid">
        <div className="jdma-vue-card">
          <h3 className="jdma-vue-card-title">Avis par collaborateur</h3>
          {loading ? (
            <div className="jdma-vue-empty">Chargement…</div>
          ) : collabFiltres.length === 0 ? (
            <div className="jdma-vue-empty">Aucun collaborateur avec avis sur cette période.</div>
          ) : (
            <div className="jdma-vue-collab-list">
              {collabFiltres.map(c => (
                <button
                  key={c.collaborateur}
                  className={`jdma-vue-collab-row ${selectedCollab?.collaborateur === c.collaborateur ? 'active' : ''}`}
                  onClick={() => setSelectedCollab(prev => prev?.collaborateur === c.collaborateur ? null : c)}
                >
                  <div className="jdma-vue-collab-name">{c.collaborateur}</div>
                  <div className="jdma-vue-collab-stats">
                    <span className="jdma-vue-collab-note">{c.note_moyenne?.toFixed(2) || '—'}</span>
                    <Stars note={c.note_moyenne} />
                    <span className="jdma-vue-collab-count">{c.nb_avis} avis</span>
                    {c.pct_alerte > 0 && <span className="jdma-vue-collab-alert">⚠ {c.pct_alerte}%</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="jdma-vue-card">
          <h3 className="jdma-vue-card-title">
            {selectedCollab
              ? <>Avis de {selectedCollab.collaborateur} ({avisFiltres.length}) <button className="jdma-vue-clear-collab" onClick={() => setSelectedCollab(null)} title="Voir tous les avis">✕</button></>
              : `Tous les avis (${avisFiltres.length})`}
          </h3>
          {avisFiltres.length === 0 ? (
            <div className="jdma-vue-empty">
              {avisAll.length === 0
                ? 'Aucun avis sur cette période. La synchronisation automatique est gérée dans Administration → Bases de données → Sync automatique.'
                : 'Aucun avis avec ces filtres.'}
            </div>
          ) : (
            <div className="jdma-vue-avis-list">
              {avisFiltres.map(a => {
                const d = a.data || {};
                const nom = [d['Prénom Feedbacker'], d['Nom Feedbacker']].filter(Boolean).join(' ') || '—';
                return (
                  <div key={a.id} className={`jdma-vue-avis-row note-${a.note || 'n'}`}>
                    <div className="jdma-vue-avis-head">
                      <Stars note={a.note} />
                      <span className="jdma-vue-avis-date">{formatDate(a.date_reponse)}</span>
                    </div>
                    <div className="jdma-vue-avis-client">
                      <strong>{nom}</strong>
                      {d['immatriculation'] && <> · {d['immatriculation']}</>}
                      {d['marque'] && <> · {d['marque']} {d['modele']}</>}
                    </div>
                    {a.commentaire && (
                      <blockquote className="jdma-vue-avis-comment">« {a.commentaire} »</blockquote>
                    )}
                    {Array.isArray(a.ai_themes) && a.ai_themes.length > 0 && (
                      <div className="jdma-vue-avis-ai">
                        {a.ai_themes.map((t, idx) => (
                          <span
                            key={idx}
                            className={`jdma-vue-ai-chip ${t.polarite === 'positif' ? 'positif' : t.polarite === 'négatif' ? 'negatif' : 'neutre'}`}
                            title={t.extrait || ''}
                          >
                            {t.polarite === 'positif' ? '🙂' : t.polarite === 'négatif' ? '😕' : '·'} {t.theme}
                          </span>
                        ))}
                      </div>
                    )}
                    {a.ai_resume && (
                      <div className="jdma-vue-ai-resume">🤖 {a.ai_resume}</div>
                    )}
                    <div className="jdma-vue-avis-actions">
                      <button
                        className="btn-secondary btn-sm"
                        onClick={() => setReplyAvis(a)}
                        disabled={!a.email_client && !a.telephone_client}
                        title={(!a.email_client && !a.telephone_client) ? 'Aucun contact disponible' : ''}
                      >
                        💬 Répondre
                      </button>
                      <button
                        className="jdma-comment-toggle"
                        onClick={() => setExpandedAvis(prev => prev === a.id ? null : a.id)}
                        title="Commentaires internes"
                      >
                        🗒️ {commentCounts[a.id] || 0} commentaire{(commentCounts[a.id] || 0) > 1 ? 's' : ''}
                        <span className="jdma-comment-arrow">{expandedAvis === a.id ? '▲' : '▼'}</span>
                      </button>
                    </div>
                    <div className="jdma-vue-recontact">
                      {a.recontacte_le ? (
                        <span className="jdma-recontact-done">
                          ✓ Recontacté par {CANAL_LABELS[a.canal_recontact] || a.canal_recontact} le {formatDate(a.recontacte_le)}
                          {isSuperAdmin && (
                            <button
                              className="jdma-recontact-reset"
                              onClick={() => handleRecontactReset(a)}
                              title="Annuler le recontact"
                            >✕</button>
                          )}
                        </span>
                      ) : (
                        <div className="jdma-recontact-btns">
                          <span className="jdma-recontact-label">Marquer recontacté :</span>
                          <button className="jdma-recontact-btn" onClick={() => handleRecontact(a, 'sms')}>📱 SMS</button>
                          <button className="jdma-recontact-btn" onClick={() => handleRecontact(a, 'mail')}>✉️ Mail</button>
                          <button className="jdma-recontact-btn" onClick={() => handleRecontact(a, 'telephone')}>📞 Tél.</button>
                        </div>
                      )}
                    </div>
                    {expandedAvis === a.id && (
                      <JDMAInternalComments
                        type={type}
                        avisId={a.id}
                        user={user}
                        canEdit={isSuperAdmin || user?.role === 'admin' || user?.role === 'operateur'}
                        onCountChange={updateCommentCount}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <JDMAReplyModal
        open={!!replyAvis}
        avis={replyAvis}
        type={type}
        onClose={() => setReplyAvis(null)}
        onSent={() => showToast('Réponse envoyée', 'success')}
      />

      {toast && <div className={`jdma-vue-toast ${toast.kind}`}>{toast.msg}</div>}
    </div>
  );
}

function KPICard({ label, big, subtitle, color = 'neutral' }) {
  return (
    <div className={`jdma-kpi jdma-kpi-${color}`}>
      <div className="jdma-kpi-label">{label}</div>
      <div className="jdma-kpi-big">{big}</div>
      {subtitle && <div className="jdma-kpi-sub">{subtitle}</div>}
    </div>
  );
}

// ─── Zone de commentaires internes inline (texte + 1 PJ par commentaire) ────
function JDMAInternalComments({ type, avisId, user, canEdit, onCountChange }) {
  const [comments, setComments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState('');
  const [pendingFile, setPendingFile] = useState(null);   // { file, previewUrl, type }
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Ref vers onCountChange pour casser la boucle de re-render :
  // si le parent passe une fonction inline non mémoïsée, la dépendance
  // de useCallback/useEffect resterait stable même quand l'identité change.
  const onCountChangeRef = useRef(onCountChange);
  useEffect(() => { onCountChangeRef.current = onCountChange; });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/jdma/${type}/${avisId}/commentaires`);
      const c = res.data.commentaires || [];
      const d = res.data.documents || [];
      setComments(c);
      setDocuments(d);
      onCountChangeRef.current?.(avisId, c.length);
    } catch (e) {
      console.error('Load comments:', e);
    } finally {
      setLoading(false);
    }
  }, [type, avisId]);

  useEffect(() => { load(); }, [load]);

  const handleFileChosen = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      alert('Format non supporté (image ou PDF uniquement)');
      return;
    }
    setPendingFile({
      file,
      type: file.type,
      previewUrl: URL.createObjectURL(file),
    });
  };

  const removePending = () => {
    if (pendingFile?.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl);
    setPendingFile(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileChosen(file);
  };

  const submit = async () => {
    if (!newText.trim()) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/jdma/${type}/${avisId}/commentaires`, {
        commentaire: newText.trim(),
      });
      const newComment = r.data.commentaire;

      if (pendingFile) {
        let toUpload = pendingFile.file;
        if (pendingFile.type === 'application/pdf') toUpload = await compressPdfClientSide(toUpload);
        else if (pendingFile.type.startsWith('image/')) toUpload = await compressFile(toUpload);

        const fd = new FormData();
        fd.append('file', toUpload);
        fd.append('commentaire_id', String(newComment.id));
        await api.post(`/jdma/${type}/${avisId}/documents/upload-file`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setNewText('');
      removePending();
      await load();
    } catch (e) {
      console.error('Submit comment:', e);
      alert(e.response?.data?.error || 'Erreur ajout commentaire');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commId) => {
    if (!window.confirm('Supprimer ce commentaire et ses pièces jointes ?')) return;
    try {
      await api.delete(`/jdma/${type}/${avisId}/commentaires/${commId}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur suppression');
    }
  };

  const docsFor = (commId) => documents.filter(d => d.commentaire_id === commId);
  const isAuthor = (c) => c.user_id != null && Number(c.user_id) === Number(user?.id);

  return (
    <div className="jdma-comments-zone">
      {loading ? (
        <div className="jdma-vue-empty">Chargement…</div>
      ) : (
        <>
          {comments.length === 0 ? (
            <div className="jdma-comments-empty">Aucun commentaire interne pour l'instant.</div>
          ) : (
            <ul className="jdma-comments-list">
              {comments.map(c => {
                const author = [c.user_prenom, c.user_nom].filter(Boolean).join(' ') || 'Utilisateur';
                const cDocs = docsFor(c.id);
                return (
                  <li key={c.id} className="jdma-comment-item">
                    <div className="jdma-comment-header">
                      <strong>{author}</strong>
                      <span className="jdma-comment-date">{formatDate(c.created_at)}</span>
                      {(isAuthor(c) || user?.role === 'super-admin') && (
                        <button
                          className="jdma-comment-delete"
                          onClick={() => handleDelete(c.id)}
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                    <div className="jdma-comment-text">{c.commentaire}</div>
                    {cDocs.length > 0 && (
                      <div className="jdma-comment-docs">
                        {cDocs.map(d => (
                          <CommentDocThumb key={d.id} doc={d} />
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {canEdit && (
            <div className="jdma-comment-form">
              <textarea
                className="jdma-comment-textarea"
                placeholder="Ajouter une explication interne…"
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                rows={3}
              />
              <div className="jdma-comment-form-actions">
                <div
                  className={`comment-doc-drop${dragging ? ' dragging' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => !pendingFile && fileInputRef.current?.click()}
                >
                  {pendingFile ? (
                    <CommentDocThumb doc={{ name: pendingFile.file.name, type: pendingFile.type, url: pendingFile.previewUrl }} />
                  ) : (
                    <>
                      <span className="comment-doc-icon">📎</span>
                      <span className="comment-doc-hint">Glisser un doc (image / PDF)</span>
                    </>
                  )}
                  {pendingFile && (
                    <button
                      type="button"
                      className="comment-doc-remove"
                      onClick={(e) => { e.stopPropagation(); removePending(); }}
                      title="Retirer"
                    >
                      ✕
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileChosen(e.target.files?.[0])}
                  />
                </div>
                <button
                  className="btn-primary btn-sm"
                  disabled={!newText.trim() || submitting}
                  onClick={submit}
                >
                  {submitting ? '⏳' : '✅ Ajouter'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default JDMAVue;
